import { useTranslation } from "react-i18next";

import {
    BBTIConfiguration,
    FaultCodeRecommendation,
    InsuranceConfiguration,
    LeaseConfiguration,
    NtfConfiguration,
    Test,
    TestConfiguration,
    ValidationConfiguration,
} from "./BmsCommonInterfaces";
import defaultConfiguration from "./UiConfiguration.json";
import {
    BMS_LICENSE_FEATURE_ALL,
    BMS_LICENSE_FEATURE_GATHER_IMEI,
    BMS_LICENSE_FEATURE_IDENTIFICATION_VERIFICATION,
    BMS_LICENSE_TYPE_ALL,
    bmsLicenseTypesAndFeatureKeys,
} from "components/licenses/common";
import { LicenseList } from "domain/licenses";
import * as LanguageRepository from "services/language/languageRepository";

export const MAX_APP_NAME_LENGTH = 15;

export enum AvailableApps {
    BMS = "bmsApp",
    DEVICE_INSPECT = "deviceInspectApp",
    CUSTOM = "customApp",
}

export enum AvailablePlatforms {
    APP_USDK = "appUSDK",
    APP_ISDK = "appISDK",
    BROWSER = "browser",
    SDK = "sdk",
}

export function getPlatformTypes() {
    const { t } = useTranslation();
    return [
        { value: AvailablePlatforms.APP_USDK, label: t("Configuration.genericConfigurationForm.appUSDK") },
        { value: AvailablePlatforms.APP_ISDK, label: t("Configuration.genericConfigurationForm.appISDK") },
        { value: AvailablePlatforms.BROWSER, label: t("Configuration.genericConfigurationForm.browser") },
        { value: AvailablePlatforms.SDK, label: t("Configuration.genericConfigurationForm.sdk") },
    ];
}

export function getAvailableApps() {
    const { t } = useTranslation();
    return [
        { value: AvailableApps.BMS, label: t("Configuration.genericConfigurationForm.bmsApp") },
        { value: AvailableApps.DEVICE_INSPECT, label: t("Configuration.genericConfigurationForm.deviceInspectApp") },
        { value: AvailableApps.CUSTOM, label: t("Configuration.genericConfigurationForm.customApp") },
    ];
}

export function getAppImageAndColours() {
    return [
        {
            value: AvailableApps.BMS,
            path: "/public/apps/bms.png",
            primaryColor: "#0d1bb5",
            secondaryColor: "#0d1bb5",
            tertiaryColor: "#000000",
            checkContrast: false,
        },
        {
            value: AvailableApps.DEVICE_INSPECT,
            path: "/public/apps/device-inspect.png",
            primaryColor: "#000000",
            secondaryColor: "#000000",
            tertiaryColor: "#000000",
            checkContrast: true,
        },
    ];
}

export function getFeatureName(featureKey: string, t: (key: string) => string) {
    switch (featureKey) {
        case "TradeIn":
            return t("Configuration.viewConfigureDialog.buyBackTradeInTab");
        case "Lease":
            return t("Configuration.viewConfigureDialog.leaseTab");
        case "Validation":
            return t("Configuration.viewConfigureDialog.validationTab");
        case "PreSalesInsurance":
            return t("Configuration.viewConfigureDialog.insuranceTab");
        case "FaultCheck":
            return t("Configuration.viewConfigureDialog.ntfTab");
        case "infogathering":
            return t("Configuration.viewConfigureDialog.identifierVerificationTab");
        default:
            return `${featureKey} Tests`;
    }
}

export interface FeatureTestsConfiguration {
    features: string[];
    bbtiConfiguration: BBTIConfiguration;
    validationConfiguration: ValidationConfiguration;
    insuranceConfiguration: InsuranceConfiguration;
    ntfConfiguration: NtfConfiguration;
    leaseConfiguration: LeaseConfiguration;
}

export function getPlatformTests(platform: string): TestConfiguration[] {
    switch (platform) {
        case "appUSDK":
            return defaultConfiguration.appUSDKBasedTests;
        case "appISDK":
            return defaultConfiguration.appISDKBasedTests;
        case "browser":
            return defaultConfiguration.browserBasedTests;
        case "sdk":
            return defaultConfiguration.sdkBasedTests;
        default:
            return defaultConfiguration.appUSDKBasedTests;
    }
}

export function getTestMode(testName: string, platformTests: TestConfiguration[]): string {
    const test = platformTests.find((test: { name: string }) => test.name === testName);
    if (test && test.mode && test.mode.length > 0) {
        return test.mode[0];
    }
    return "dedicated";
}

function updateTestsOnPlatformChange(
    currentEnabledTests: Test[],
    useUsdk: boolean,
    platformTests: TestConfiguration[]
): Test[] {
    currentEnabledTests.forEach((test: Test) => {
        test.usdk = useUsdk;
        test.mode = getTestMode(test.name, platformTests);
    });
    return currentEnabledTests;
}

export function updateFeatureTests(featureConfigurations: FeatureTestsConfiguration, platform: string) {
    const enabledFeature = featureConfigurations.features;
    const usdk = platform.includes("USDK");
    const platformTests = getPlatformTests(platform);

    enabledFeature.forEach((feature: string) => {
        switch (feature) {
            case "TradeIn":
                featureConfigurations.bbtiConfiguration.tests = updateTestsOnPlatformChange(
                    featureConfigurations.bbtiConfiguration.tests,
                    usdk,
                    platformTests
                );
                break;
            case "PreSalesInsurance":
                featureConfigurations.insuranceConfiguration.tests = updateTestsOnPlatformChange(
                    featureConfigurations.insuranceConfiguration.tests,
                    usdk,
                    platformTests
                );
                break;
            case "Validation":
                featureConfigurations.validationConfiguration.tests = updateTestsOnPlatformChange(
                    featureConfigurations.validationConfiguration.tests,
                    usdk,
                    platformTests
                );
                break;
            case "FaultCheck":
                featureConfigurations.ntfConfiguration.tests = updateTestsOnPlatformChange(
                    featureConfigurations.ntfConfiguration.tests,
                    usdk,
                    platformTests
                );
                break;
            case "Lease":
                featureConfigurations.leaseConfiguration.tests = updateTestsOnPlatformChange(
                    featureConfigurations.leaseConfiguration.tests,
                    usdk,
                    platformTests
                );
                break;
        }
    });

    return featureConfigurations;
}

export function selectLanguageForConditionalQuestions(
    bmpLanguage: string
): keyof typeof defaultConfiguration.questions {
    const normalizedLanguage = bmpLanguage.toLowerCase();
    const languages = Object.keys(defaultConfiguration.questions);

    // Check if the specified language exists in the configuration
    if (languages.includes(normalizedLanguage)) {
        return normalizedLanguage as keyof typeof defaultConfiguration.questions;
    } else {
        // Default to "en" if the specified language is not found
        return "en";
    }
}

export function selectLanguageForFaultCodes(bmpLanguage: string): keyof typeof defaultConfiguration.fault_codes {
    const normalizedLanguage = bmpLanguage.toLowerCase();
    const languages = Object.keys(defaultConfiguration.fault_codes);

    return languages.includes(normalizedLanguage)
        ? (normalizedLanguage as keyof typeof defaultConfiguration.fault_codes)
        : "en";
}

export function fetchFeaturesWithAvailableLicense(licenseList: LicenseList) {
    const featuresWithAvailableLicense = [];
    const hasAllFeaturesLicenses = licenseList.licenses.licenseData.some(
        (license) => license.type === BMS_LICENSE_TYPE_ALL
    );
    if (hasAllFeaturesLicenses) {
        featuresWithAvailableLicense.push(BMS_LICENSE_FEATURE_ALL);
        return featuresWithAvailableLicense;
    }

    for (const { type, feature } of bmsLicenseTypesAndFeatureKeys) {
        if (
            licenseList.licenses.licenseData.some(
                (license: { type: string; available: number }) => license.type === type
            )
        ) {
            featuresWithAvailableLicense.push(feature);
        }
    }

    if (featuresWithAvailableLicense.length > 0) {
        featuresWithAvailableLicense.push(BMS_LICENSE_FEATURE_GATHER_IMEI);
        featuresWithAvailableLicense.push(BMS_LICENSE_FEATURE_IDENTIFICATION_VERIFICATION);
    }

    return featuresWithAvailableLicense;
}

type AppLink = {
    androidLink: string;
    iosLink: string;
    autoForward: boolean;
};

const appUrls: Record<string, AppLink> = {
    bmsApp: {
        androidLink: "https://play.google.com/store/apps/details?id=com.blancco.core",
        iosLink: "https://apps.apple.com/app/id1080632893",
        autoForward: false,
    },
    deviceInspectApp: {
        androidLink: "https://play.google.com/store/apps/details?id=com.device.inspect",
        iosLink: "https://apps.apple.com/app/id6476104398",
        autoForward: false,
    },
    browser: {
        androidLink: "https://deviceinspect-bmp.mobilesolutions.ai?apikey={PIN_NUMBER}",
        iosLink: "https://deviceinspect-bmp.mobilesolutions.ai?apikey={PIN_NUMBER}",
        autoForward: true,
    },
    customApp: { androidLink: "https://", iosLink: "https://", autoForward: false },
};

export function getAppStoreUrl(selectedApp: string) {
    return appUrls[selectedApp];
}

export function getDefaultRecommendations() {
    return getDefaultLanguageRecommendations(LanguageRepository.getLanguage().locale);
}

function getDefaultLanguageRecommendations(locale: string) {
    const allRecommendations: FaultCodeRecommendation[] = [];
    const recommendationKeys = new Set<string>();
    const selectedLanguage = selectLanguageForFaultCodes(locale);

    defaultConfiguration.fault_codes[selectedLanguage].forEach(({ recommendations }) => {
        recommendations.forEach((recommendation) => {
            if (!recommendationKeys.has(recommendation.key)) {
                recommendationKeys.add(recommendation.key);
                allRecommendations.push(recommendation);
            }
        });
    });

    return allRecommendations;
}

export function getDefaultRecommendation(language: string, key: string) {
    const langKey = language as keyof typeof defaultConfiguration.fault_codes;
    const recommendation = getDefaultLanguageRecommendations(langKey).find((item) => item.key === key);

    return recommendation;
}

export function isDefaultRecommendation(key: string) {
    return getDefaultRecommendations().some((rec) => rec.key === key);
}

export function getDefaultLanguageFaultcodes(laguage: string) {
    const selectedLanguage = selectLanguageForFaultCodes(laguage);
    return defaultConfiguration.fault_codes[selectedLanguage];
}

export function getComplementaryColor(hexColor: string) {
    hexColor = hexColor.replace("#", "");
    const r = parseInt(hexColor.substring(0, 2), 16);
    const g = parseInt(hexColor.substring(2, 4), 16);
    const b = parseInt(hexColor.substring(4, 6), 16);

    const compR = 255 - r;
    const compG = 255 - g;
    const compB = 255 - b;

    return (
        "#" +
        compR.toString(16).padStart(2, "0") +
        compG.toString(16).padStart(2, "0") +
        compB.toString(16).padStart(2, "0")
    );
}
